import { LIBRARY_PATHS } from 'components/assets/libraries'
import {
  BookSparkIcon,
  ChevronIcon,
  CloseIcon,
  CubeIcon,
  HelpIcon,
  StacksIcon,
  TeamIcon,
} from 'components/icons'
import CurrentUserMenu from 'components/layout/current-user-menu'
import LoadingUser from 'components/layout/loading-user'
import Logo from 'components/layout/logo'
import LogoMark from 'components/layout/logo-mark'
import SectionLink from 'components/layout/section-link'
import TrialTicker from 'components/layout/trial-ticker'
import UserAvatar from 'components/utilities/user-avatar'
import { useApp } from 'context/app-context'
import { useCurrentUser } from 'context/current-user-context'
import classNames from 'helpers/class-names'
import { PresentedTeamMember } from 'lib/presenters/team-member-presenter'
import Link from 'next/link'
import { memo } from 'react'
import useSWR from 'swr'
import { Menu, Tooltip } from 'ui'

interface Data {
  user: PresentedTeamMember
}

const Navigation = memo(() => {
  const {
    navigationCollapsed,
    toggleNavigation,
    toggleMobileNavigation,
    mobileNavigationClosed,
  } = useApp()
  const { uuid, email, firstName, team, isTrial, isPromo, isAdmin } =
    useCurrentUser()
  const { data } = useSWR<Data>(uuid ? `/api/users/${uuid}` : null)

  const UserButton = ({ open }) => (
    <div className="mt-4 flex w-full border-t border-gray-400 pl-4 transition duration-300 ease-in-out hover:bg-gray-600/75 focus:bg-opacity-75 focus:outline-none focus-visible:bg-gray-600 dark:border-gray-600">
      <div className="py-4">
        <div className="h-10 w-10 overflow-hidden rounded-full border-2 border-gray-400 focus:outline-none group-focus-visible:border-info group-focus-visible:border-opacity-75 dark:border-gray-500">
          <UserAvatar email={String(email)} className="h-full w-full" />
        </div>
      </div>
      {!navigationCollapsed && (
        <div className="flex w-full items-center py-3.5">
          <div className="flex-1 pl-2 text-left leading-5">
            <div className="w-full break-all font-semibold text-white">
              {data?.user.firstName || firstName}
            </div>
            <div className="line-clamp-1 w-full text-sm text-gray-200">
              {team}
            </div>
          </div>
          <ChevronIcon
            className={classNames(
              'mr-2.5 w-6 text-gray-300 transition-all duration-300 ease-in-out group-hover:text-gray-50 group-focus-visible:text-gray-50',
              {
                'rotate-180': open,
              },
            )}
          />
        </div>
      )}
    </div>
  )

  return (
    <nav
      className={classNames(
        'fixed flex h-full w-56 flex-col bg-gray-600 transition duration-300 ease-in-out dark:bg-gray-900 md:bg-gray-500 md:transition-none',
        {
          'md:w-56': !navigationCollapsed,
          'md:w-18': navigationCollapsed,
          'translate-x-0 md:translate-x-0': !mobileNavigationClosed,
          '-translate-x-full md:translate-x-0': mobileNavigationClosed,
        },
      )}>
      <div className="md:mt-1 md:p-4">
        <div className="w-full py-4 pl-2 text-left md:hidden">
          <button
            onClick={() => toggleMobileNavigation()}
            className="group -mt-2 rounded-lg p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75">
            <CloseIcon className="h-6 w-6 text-gray-300 hover:text-white group-focus-visible:text-white" />
          </button>
        </div>
        <Link href="/">
          <a
            title="Toolpath"
            className="hidden rounded p-1 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75 md:inline-block">
            <Logo
              className={classNames('hidden md:inline-block md:w-full', {
                'md:hidden': navigationCollapsed,
              })}
            />
            <LogoMark
              className={classNames('hidden w-8', {
                'md:inline-block': navigationCollapsed,
              })}
            />
          </a>
        </Link>
      </div>
      <div className="flex-1 md:mt-6">
        <SectionLink href="/projects" icon={CubeIcon} otherHrefs={['parts']}>
          Projects
        </SectionLink>
        <SectionLink href="/cut-configs" icon={BookSparkIcon}>
          Cut Configs
        </SectionLink>
        <SectionLink
          href="/tool-libraries"
          icon={StacksIcon}
          otherHrefs={LIBRARY_PATHS}>
          Libraries
        </SectionLink>
        <SectionLink
          href="/team"
          icon={TeamIcon}
          otherHrefs={['users', 'billing', 'team']}>
          Team
        </SectionLink>
      </div>
      <div className="flex-0 hidden md:inline-block">
        <Tooltip tip={navigationCollapsed ? 'Help' : null} placement="right">
          <div className="px-4">
            <a
              href="https://support.toolpath.com"
              target="_blank"
              className={classNames(
                'group flex w-full items-center rounded py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
              )}>
              <div className="ml-2 flex h-6 w-6 items-center rounded-full border-2 border-gray-300 text-center text-gray-300 transition-all duration-300 ease-in-out group-hover:border-gray-50 group-hover:text-gray-50">
                <HelpIcon className={classNames('mx-auto w-4')} />
              </div>
              {!navigationCollapsed && (
                <div className="ml-3 pr-2 font-semibold tracking-wide text-gray-300 transition-colors duration-300 ease-in-out group-hover:text-gray-50">
                  Help
                </div>
              )}
            </a>
          </div>
        </Tooltip>
        <Tooltip tip={navigationCollapsed ? 'Expand' : null} placement="right">
          <div className="px-4 pt-1.5">
            <button
              type="button"
              data-cy="toggle-navigation-collapsed"
              className={classNames(
                'group flex w-full items-center rounded py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75',
              )}
              onClick={() => toggleNavigation()}>
              <ChevronIcon
                className={classNames(
                  'ml-2 w-6 rounded-full border-2 border-gray-300 text-gray-300 transition-all duration-300 ease-in-out group-hover:border-gray-50 group-hover:text-gray-50',
                  {
                    'rotate-180': !navigationCollapsed,
                  },
                )}
              />
              {!navigationCollapsed && (
                <div className="ml-3 pr-2 font-semibold tracking-wide text-gray-300 transition-colors duration-300 ease-in-out group-hover:text-gray-50">
                  Collapse
                </div>
              )}
            </button>
          </div>
        </Tooltip>
        {isTrial && <TrialTicker />}
        {!email && <LoadingUser />}
        {email && (
          <Menu
            className="-mb-1.5"
            buttonClass="w-full h-full focus-visible:ring-0 group"
            trigger={UserButton}
            offset={
              navigationCollapsed
                ? [0, isAdmin && !isPromo ? -105 : -70]
                : [14, isAdmin && !isPromo ? -105 : -70]
            }
            placement="right-start">
            <CurrentUserMenu />
          </Menu>
        )}
      </div>
    </nav>
  )
})

export default Navigation
